import "./EventAnalyticsSectionView.styl";
import { getWaffleEventFacets, getWaffleEvents, timediff } from "../_resources/waffle-helpers.js";
import { createJSONEditor } from "vanilla-jsoneditor/standalone.js";

const HTTP_STATUS_LABELS = {
  200: "OK",
  302: "Redirect",
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not Found",
  500: "Server Error",
};

function statusEmoji(v) {
  if (v == null) return "";
  if (v >= 500) return "❌";
  if (v >= 400) return "⚠️";
  if (v >= 300) return "⏩️";
  if (v >= 200) return "✅";
  return "";
}

const EVENT_CODE_LABELS = {
  // happy path
  RESOLVED_QUERY:             "✅ Resolved query",
  SERVING_ASSET:              "✅ Serving asset",
  BOMB_QUERY_GUARD:           "🛡️ Bomb query guard",
  // client errors
  NO_DATASET_GIVEN:           "⚠️ No dataset given",
  DATASET_NOT_CONFIGURED:     "⚠️ Dataset not configured",
  BRANCH_NOT_CONFIGURED:      "⚠️ Branch not configured",
  DATASET_NOT_FOUND:          "⚠️ Dataset not found",
  DATASET_UNAUTHORIZED:       "⚠️ Dataset unauthorized",
  SYNC_UNAUTHORIZED:          "⚠️ Sync unauthorized",
  VALIDATE_UNAUTHORIZED:      "⚠️ Validate unauthorized",
  VALIDATE_NO_SLUG:           "⚠️ Validate — no slug",
  NO_QUERY_PROVIDED:          "⚠️ No query provided",
  QUERY_PARSING_ERROR:        "⚠️ Query parsing error",
  QUERY_STRUCTURE_ERROR:      "⚠️ Query structure error",
  QUERY_DEFINITION_ERROR:     "⚠️ Query definition error",
  ASSET_NOT_PROVIDED:         "⚠️ Asset not provided",
  // server errors
  DEFAULT_COMMIT_NOT_RESOLVED:"❌ Default commit not resolved",
  NO_READER_INSTANCE:         "❌ No reader instance",
  UNKNOWN_ERROR:              "❌ Unknown error",
};

const FACET_COLUMNS = [
  { key: "type",        label: "Type" },
  { key: "api_version", label: "API version", allowEmpty: true },
  { key: "status",      label: "Status" },
  { key: "datasetSlug", label: "Dataset" },
  { key: "branch",      label: "Branch" },
  { key: "event_code",  label: "Event code" },
  { key: "asset",       label: "Asset" },
  { key: "query_from",  label: "Query .from",  allowEmpty: true },
  { key: "referer",     label: "Referer",     allowEmpty: true },
];



function buildTestUrl(serverUrl, record) {
  if (!serverUrl || !record.datasetSlug || !record.branch) return null;
  const base = serverUrl.replace(/\/$/, "");
  const { api_version, datasetSlug, branch, queryString, asset } = record;
  const v = api_version === "v3" ? "v3" : "v2"; // default to v2 for null/old events
  if (asset) {
    return `${base}/${v}/${datasetSlug}/${branch}/assets/${asset}`;
  }
  if (queryString) {
    if (api_version === "v1") return `${base}/${datasetSlug}/${branch}?${queryString}`;
    return `${base}/${v}/${datasetSlug}/${branch}?${queryString}`;
  }
  return null;
}

function formatQuerySummary(queryString, query_from) {
  if (!queryString) return "";

  if (!query_from) return decodeURIComponent(queryString);

  if (query_from === "concepts.schema" || query_from === "datapoints.schema" || query_from === "entities.schema")
    return "Schema query";

  if (query_from === "concepts")
    return "CONCEPT PROPERTIES";

  let parsed;
  try { parsed = urlon2.parse(decodeURIComponent(queryString)); } catch (e) {
    try { parsed = urlon.parse(decodeURIComponent(queryString)); } catch (e2) {
      try { parsed = JSON.parse(queryString); } catch (e3) { return queryString; }
    }
  }
  if (!parsed || typeof parsed !== "object") return queryString;

  const key = parsed.select?.key;
  const value = parsed.select?.value;

  if (query_from === "entities") {
    const keyStr = Array.isArray(key) ? key.join(" ") : (key ?? "");
    return keyStr || queryString;
  }

  if (query_from === "datapoints") {
    const DIM_EMOJI = ["0️⃣","1️⃣","2️⃣","3️⃣","4️⃣","5️⃣","6️⃣","7️⃣","8️⃣","9️⃣","🔟"];
    const nDims = Array.isArray(key) ? key.length : (key ? 1 : 0);
    const dimLabel = DIM_EMOJI[nDims] ?? `${nDims}`;
    const valueStr = Array.isArray(value) ? value.join(" ") : (value ?? "");
    return nDims ? `${dimLabel} — ${valueStr}` : (valueStr || queryString);
  }

  // fallback: generic summary
  if (!key && !value) return queryString;
  const keyStr = Array.isArray(key) ? key.join(" ") : (key ?? "");
  const valueStr = Array.isArray(value) ? value.join(" ") : (value ?? "");
  if (keyStr && valueStr) return `${keyStr}: ${valueStr}`;
  return keyStr || valueStr || queryString;
}

export default function EventAnalyticsSectionView({ dom, message, getToken }) {
  const { bad } = message;
  const container = dom;

  const sectionHeader = container.append("div").class("admin-section-header");
  const headerLeft = sectionHeader.append("div").class("events-header-left");
  headerLeft.append("h1").text("Event Analytics");
  const totalSummary = headerLeft.append("div").class("events-total");
  const legendWrap = sectionHeader.append("div").class("events-legend-wrap").style("display", "none");

  const statusLine = container.append("div").class("events-status");

  const facetsGrid = container.append("div").class("events-facets-grid");

  // Build one select per filterable column — all in one row
  const selects = {};
  FACET_COLUMNS.forEach(({ key, label }) => {
    const cell = facetsGrid.append("div").class("events-facet-cell");
    cell.append("label").class("events-facet-label").text(label);
    selects[key] = cell.append("select").class("input events-facet-select").attr("disabled", true);
  });

  // Status line only — no manual load button
  const actionsRow = container.append("div").class("events-actions");
  const tableStatusLine = actionsRow.append("span").class("events-table-status");

  // Chart and events table
  const chartWrap = container.append("div").class("events-chart-wrap");
  const tableWrap = container.append("div").class("events-table-wrap");

  let _serverUrl = null;
  let _colorScale = null; // shared between renderChart and renderTable
  let _knownDatasetSlugs = null; // Set of known dataset slugs on the server
  let _facetsLoaded = false;
  let _facetsTotal = null; // { uniqueEvents, totalCount } for current filter
  let _facetsData = null;  // full facets response (per-column value arrays)
  let _requestToken = 0; // incremented on every render(); stale responses are dropped
  let _lastRows = null; // stored to allow re-render on theme change

  const _themeObserver = new MutationObserver(() => {
    if (_lastRows) {
      renderChart(_lastRows);
      renderTable(_lastRows);
    }
  });
  _themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

  const TABLE_COLUMNS = [
    { key: "count",        label: "Hits",           fmt: n => n?.toLocaleString() ?? ""},
    { key: "type",         label: "Type",           fmt: v => v ?? "" },
    { key: "api_version",  label: "API",            fmt: v => v ?? "" },
    { key: "status",       label: "Status",
      renderCell: (td, record) => {
        const v = record.status;
        td.text(v != null ? `${statusEmoji(v)} ${v}` : "");
        if (v != null) {
          const group = Math.floor(v / 100);
          const cls = { 2: "2xx", 3: "3xx", 4: "4xx", 5: "5xx" }[group];
          if (cls) td.class(`events-td-status-${cls}`);
        }
      }
    },
    { key: "datasetSlug",  label: "Dataset",
      renderCell: (td, record, _sv, knownSlugs) => {
        const v = record.datasetSlug ?? "";
        td.text(v);
        if (v && knownSlugs && !knownSlugs.has(v))
          td.class("events-td-unknown-dataset");
      }
    },
    { key: "branch",       label: "Branch",         fmt: v => v ?? "" },
    { key: "comment",      label: "Comment",        fmt: v => v ?? "" },
    { key: "earliest_ms",  label: "First seen",
      renderCell: (td, record) => {
        const v = record.earliest_ms;
        if (!v) return;
        const iso = new Date(v).toISOString().replace("T", " ").slice(0, 19);
        td.attr("title", iso).text(timediff(v));
      }
    },
    { key: "latest_ms",    label: "Last seen",
      renderCell: (td, record) => {
        const v = record.latest_ms;
        if (!v) return;
        const iso = new Date(v).toISOString().replace("T", " ").slice(0, 19);
        td.attr("title", iso).text(timediff(v));
      }
    },
    { key: "timing",       label: "Timing ms",
      renderCell: (td, record) => {
        const v = record.timing;
        if (v != null && _colorScale) {
          td.text(v.toFixed(0))
            .style("font-weight", "bold")
            .style("text-align", "right")
            .style("background", `linear-gradient(to left, var(--bg-2) 70%, ${_colorScale(v)} 30%)`);
        }
      }
    },
    { key: "query_from",   label: "From",           fmt: v => v ?? "" },
    { key: "queryString",  label: "Query or Asset",
      renderCell: (td, record) => {
        const v = record.queryString;
        const text = record.asset ? record.asset : formatQuerySummary(v, record.query_from);
        td.text(text);
        if (v) {
          td.attr("title", decodeURIComponent(v));
          td.class("events-td-query").on("click", () => showQueryPopup(v));
        }
      }
    },
    { key: "referer",      label: "Referer",        fmt: v => v ?? "" },
    { key: "_testUrl",     label: "Test",
      renderCell: (td, record, serverUrl) => {
        const url = buildTestUrl(serverUrl, record);
        if (!url) return;
        td.append("a")
          .attr("href", url)
          .attr("target", "_blank")
          .attr("rel", "noopener noreferrer")
          .class("events-td-test-link")
          .text("▶");
      }
    },
  ];

  function setStatusLine(text) {
    statusLine.text(text).style("display", text ? null : "none");
  }

  function onFilterChange() {
    if (_serverUrl) loadEvents(_serverUrl, ++_requestToken);
  }

  function getActiveFilters() {
    const params = { limit: 100, orderBy: "count:desc" };
    FACET_COLUMNS.forEach(({ key }) => {
      const val = selects[key].node()?.value;
      if (val) params[key] = val;  // includes '__empty__' sentinel
    });
    return params;
  }

  function renderFacets(facets) {
    _facetsTotal = facets._total;
    _facetsData = facets;
    const total = _facetsTotal;
    totalSummary.text(
      total.uniqueEvents
        ? `Total ${total.uniqueEvents.toLocaleString()} unique events · ${(total.totalCount || 0).toLocaleString()} total hits`
        : "No events"
    );

    FACET_COLUMNS.forEach(({ key, allowEmpty }) => {
      const sel = selects[key];
      const values = facets[key] || [];
      sel.attr("disabled", values.length === 0 && !allowEmpty ? true : null);
      sel.html("");
      sel.append("option").attr("value", "").text(
        values.length === 0 && !allowEmpty
          ? "—"
          : `All (${values.reduce((s, v) => s + v.totalCount, 0).toLocaleString()} hits)`
      );
      if (allowEmpty) {
        sel.append("option").attr("value", "__empty__").text("(empty)");
      }
      values.forEach(({ value, totalCount, uniqueEvents }) => {
        const displayValue = key === "status"
          ? `${statusEmoji(value)} ${value}${HTTP_STATUS_LABELS[value] ? " " + HTTP_STATUS_LABELS[value] : ""}`
          : key === "event_code" && EVENT_CODE_LABELS[value]
            ? EVENT_CODE_LABELS[value]
            : value;
        const label = `${displayValue}  ·  ${totalCount.toLocaleString()} hits  /  ${uniqueEvents.toLocaleString()} unique`;
        sel.append("option").attr("value", value).text(label);
      });
    });
  }

  function highlightChartLine(hash) {
    chartWrap.select(`g[data-hash="${hash}"] .events-line-vis`)
      .attr("stroke-width", 3).attr("opacity", 1);
  }
  function unhighlightChartLine(hash) {
    chartWrap.select(`g[data-hash="${hash}"] .events-line-vis`)
      .attr("stroke-width", 1.5).attr("opacity", 0.75);
  }
  function highlightTableRow(hash) {
    tableWrap.selectAll(`tr[data-hash="${hash}"]`).classed("events-tr-highlight", true);
  }
  function unhighlightTableRow(hash) {
    tableWrap.selectAll(`tr[data-hash="${hash}"]`).classed("events-tr-highlight", false);
  }

  function renderChart(allRows) {
    chartWrap.html("");
    if (!allRows.length) return;

    const containerWidth = chartWrap.node().getBoundingClientRect().width || 800;
    const margin = { top: 8, right: 20, bottom: 30, left: 20 };
    const rowPitch = 3;
    const chartAreaH = allRows.length * rowPitch;
    const innerW = containerWidth - margin.left - margin.right;
    const totalH = margin.top + chartAreaH + margin.bottom;

    const xMin = d3.min(allRows, ([, r]) => r.earliest_ms);
    const xMax = d3.max(allRows, ([, r]) => r.latest_ms);
    const xScale = d3.scaleTime()
      .domain([new Date(xMin), new Date(xMax)])
      .range([0, innerW]);

    const svg = chartWrap.append("svg")
      .attr("width", containerWidth)
      .attr("height", totalH);

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const timingMin = d3.min(allRows, ([, r]) => r.timing ?? 0);
    const timingMax = d3.max(allRows, ([, r]) => r.timing ?? 0);
    const isLight = document.documentElement.getAttribute("data-theme") === "light";
    const interpolator = isLight ? d3.interpolateOrRd : d3.interpolateYlGn;
    const domain = isLight ?  [timingMin, timingMax] : [timingMax, timingMin];
    _colorScale = d3.scaleSequential(interpolator).domain(domain);
    const colorScale = _colorScale;

    const brushY = chartAreaH + 2;
    const brushH = margin.bottom - 4; // covers axis + most of margin

    // Event lines: horizontal bar from earliest_ms to latest_ms
    allRows.forEach(([hash, record], i) => {
      const y = i * rowPitch + 1.5;
      const x1 = xScale(record.earliest_ms);
      const x2 = Math.max(x1 + 2, xScale(record.latest_ms));
      const color = record.timing != null ? colorScale(record.timing) : "var(--txt-1)";
      const lineGroup = g.append("g").attr("data-hash", hash);
      lineGroup.append("line")
        .class("events-line-vis")
        .attr("x1", x1).attr("y1", y)
        .attr("x2", x2).attr("y2", y)
        .attr("stroke", color)
        .attr("stroke-width", 1.5)
        .attr("opacity", 0.75);
      // Invisible wide hit area for hover
      lineGroup.append("line")
        .attr("x1", x1).attr("y1", y)
        .attr("x2", x2).attr("y2", y)
        .attr("stroke", "transparent")
        .attr("stroke-width", 8)
        .on("mouseenter", () => { highlightChartLine(hash); highlightTableRow(hash); })
        .on("mouseleave", () => { unhighlightChartLine(hash); unhighlightTableRow(hash); });
    });

    // X axis — drawn before brush so brush overlays it in Z order
    g.append("g")
      .class("events-axis")
      .attr("transform", `translate(0,${chartAreaH + 6})`)
      .call(d3.axisBottom(xScale).ticks(6).tickSizeOuter(0));

    // Brush — appended after axis so it sits on top (Z), covering axis + margin
    const brush = d3.brushX()
      .extent([[0, brushY], [innerW, brushY + brushH]])
      .on("brush end", ({ selection }) => {
        if (!selection) {
          renderTable(allRows);
          return;
        }
        const [t0, t1] = selection.map(x => xScale.invert(x).getTime());
        const filtered = allRows.filter(([, r]) => r.latest_ms >= t0 && r.latest_ms <= t1);
        renderTable(filtered);
      });

    g.append("g").class("events-brush").call(brush);

    // Color legend in header (right-aligned), not in SVG
    if (timingMin !== timingMax) {
      legendWrap.style("display", null).html("");
      const legendW = 160;
      const legendH = 10;
      const gradId = "events-timing-grad";
      const svgLeg = legendWrap.append("svg")
        .attr("width", legendW + 40)
        .attr("height", legendH + 40);
      const defs = svgLeg.append("defs");
      const grad = defs.append("linearGradient").attr("id", gradId);
      const domain = isLight ? [0,1] : [1,0];
      const colorScale = d3.scaleSequential(interpolator).domain(domain);
      d3.range(0, 1.01, 0.05).forEach(t => {
        grad.append("stop")
          .attr("offset", `${(t * 100).toFixed(0)}%`)
          .attr("stop-color", colorScale(t));
      });
      const lg = svgLeg.append("g").attr("transform", "translate(20,20)");
      lg.append("rect")
        .attr("width", legendW).attr("height", legendH)
        .attr("y", 3)
        .attr("rx", 2)
        .attr("fill", `url(#${gradId})`);
      lg.append("text")
        .attr("y", legendH + 15).attr("font-size", 11).attr("fill", "var(--txt-1)")
        .text(`${timingMin.toFixed(0)} ms`);
      lg.append("text")
        .attr("x", legendW).attr("y", legendH + 15).attr("font-size", 11)
        .attr("text-anchor", "end").attr("fill", "var(--txt-1)")
        .text(`${timingMax.toFixed(0)} ms`);
      lg.append("text")
        .attr("x", legendW / 2).attr("y", 0).attr("font-size", 11)
        .attr("text-anchor", "middle").attr("fill", "var(--txt-1)")
        .text("timing ms");
    } else {
      legendWrap.style("display", "none");
    }
  }

  function showQueryPopup(queryString) {
    let parsed;
    try { parsed = urlon2.parse(decodeURIComponent(queryString)); } catch (e) {
      try { parsed = urlon.parse(decodeURIComponent(queryString)); } catch (e2) {
        try { parsed = JSON.parse(queryString); } catch (e3) { parsed = queryString; }
      }
    }

    d3.select(".admin-page").classed("blurry", true);
    const overlay = d3.select(".access-control-dialog")
      .style("visibility", "visible")
      .html("");

    overlay.append("div").class("blur")
      .on("click", closePopup);

    const box = overlay.append("div").class("box events-query-popup");
    box.append("div").class("closecross").text("✕").on("click", closePopup);
    box.append("div").class("header").text("Query");
    const isLight = document.documentElement.getAttribute("data-theme") === "light";
    const editorHost = box.append("div").class(`events-query-editor${isLight ? "" : " jse-theme-dark"}`).node();

    createJSONEditor({
      target: editorHost,
      props: {
        readOnly: true,
        content: { json: parsed },
        onRenderMenu: () => [],
      }
    });

    function closePopup() {
      d3.select(".admin-page").classed("blurry", false);
      d3.select(".access-control-dialog").style("visibility", "hidden").html("");
    }
  }

  function renderTable(rows) {
    tableWrap.html("");
    tableStatusLine.text("");
    if (!rows.length) {
      totalSummary.text(_facetsTotal?.uniqueEvents ? `No events match the current filter.` : "No events");
      return;
    }
    const n = rows.length;
    // When exactly one filter is active, look up its count from facets for an accurate "out of k"
    const activeFilters = FACET_COLUMNS
      .map(({ key }) => ({ key, val: selects[key].node()?.value }))
      .filter(({ val }) => !!val);
    let k = null;
    if (activeFilters.length === 1 && _facetsData) {
      const { key, val } = activeFilters[0];
      const entry = (_facetsData[key] || []).find(e => String(e.value) === val);
      if (entry) k = entry.uniqueEvents;
    }
    const filterSuffix = activeFilters.length > 0 ? " for current filter" : "";
    const shown = k != null && n < k
      ? `Showing top ${n.toLocaleString()} most frequent events out of ${k.toLocaleString()}${filterSuffix}. `
      : k != null
        ? `Showing all ${n.toLocaleString()} events${filterSuffix}. `
        : n < (_facetsTotal?.uniqueEvents ?? n)
          ? `Showing top ${n.toLocaleString()} most frequent events${filterSuffix}. `
          : `Showing all ${n.toLocaleString()} events${filterSuffix}. `;
    const hits = _facetsTotal
      ? `Total ${_facetsTotal.uniqueEvents.toLocaleString()} unique events · ${(_facetsTotal.totalCount || 0).toLocaleString()} total hits`
      : "";
    totalSummary.text(shown + hits);

    const table = tableWrap.append("table").class("admin-table events-table");
    const thead = table.append("thead").append("tr");
    TABLE_COLUMNS.forEach(({ label }) => thead.append("th").text(label));

    const tbody = table.append("tbody");
    rows.forEach(([hash, record]) => {
      const tr = tbody.append("tr")
        .attr("data-hash", hash)
        .on("mouseenter", () => { highlightChartLine(hash); highlightTableRow(hash); })
        .on("mouseleave", () => { unhighlightChartLine(hash); unhighlightTableRow(hash); });
      TABLE_COLUMNS.forEach(({ key, fmt, renderCell }) => {
        const td = tr.append("td");
        if (renderCell) {
          renderCell(td, record, _serverUrl, _knownDatasetSlugs);
        } else {
          td.text(fmt(record[key], record));
        }

      });
    });
  }

  async function loadFacets(serverUrl, token) {
    setStatusLine("Loading…");
    totalSummary.text("");
    tableWrap.html("");
    tableStatusLine.text("");
    FACET_COLUMNS.forEach(({ key }) => {
      selects[key].html("").attr("disabled", true);
    });
    const { ok, error } = await getWaffleEventFacets(serverUrl, getToken());
    if (token !== _requestToken) return; // superseded by a newer request
    if (error) {
      setStatusLine("Failed to load event facets.");
      bad("Failed to load event facets: " + error);
      return;
    }
    setStatusLine("");
    renderFacets(ok);
    // Set default filter: status=500 on first load only
    if (!_facetsLoaded) {
      const statusOpts = selects["status"].node().options;
      const has500 = Array.from(statusOpts).some(o => o.value === "500");
      if (has500) selects["status"].property("value", "500");
      _facetsLoaded = true;
    }
    // Wire up auto-reload on filter change now that selects are populated
    FACET_COLUMNS.forEach(({ key }) => {
      selects[key].on("change", onFilterChange);
    });
    loadEvents(serverUrl, token);
  }

  async function loadEvents(serverUrl, token) {
    tableStatusLine.text("Loading…");
    chartWrap.html("");
    tableWrap.html("");
    const { ok, error } = await getWaffleEvents(serverUrl, getToken(), getActiveFilters());
    if (token !== _requestToken) return; // superseded by a newer request
    if (error) {
      tableStatusLine.text("Failed to load events.");
      bad("Failed to load events: " + error);
      return;
    }
    _lastRows = ok;
    renderChart(ok);
    renderTable(ok);
  }

  function render({ selectedServerUrl, serverDatasets = [] }) {
    _serverUrl = selectedServerUrl;
    _knownDatasetSlugs = serverDatasets.length ? new Set(serverDatasets.map(d => d.slug)) : null;
    _facetsLoaded = false;
    const token = ++_requestToken;
    loadFacets(selectedServerUrl, token);
  }

  function renderError(msg) {
    setStatusLine(msg);
    totalSummary.text("");
  }

  function teardown() {
    ++_requestToken; // cancel any in-flight requests
    _facetsTotal = null;
    _facetsData = null;
    _knownDatasetSlugs = null;
    _lastRows = null;
    chartWrap.html("");
    tableWrap.html("");
    tableStatusLine.text("");
    _themeObserver.disconnect();
  }

  return { render, renderError, teardown };
}
